import { useAlerts } from '../alerts/AlertsProvider';
import { createInventoryMetadataCache } from '../collection/inventoryMetadataCache';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { CollectionResolution } from '../collection/collectionSessionService';
import { AuthenticatedUser, AuthenticationStatus } from '../../domain/auth';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { useFeedback } from '../feedback/FeedbackProvider';
import { subscribeToDeletedItems } from '../../repositories/deletedItemSubscription';
import { subscribeToActiveItems } from '../../repositories/itemSubscription';
import { subscribeToLocations } from '../../repositories/locationSubscription';
import { accountProvisioningService } from '../../services/accountProvisioningService';
import {
  authErrorMessage,
  isExpiredAuthSession,
} from '../../services/authErrors';
import { authService } from '../../services/authService';
import { logger } from '../../services/logger';
import { userProfileResolverService } from '../../services/userProfileResolverService';
import {
  firebaseCollectionRepository,
  isExpectedInaccessibleCollectionError,
} from '../../repositories/collectionRepository';
import {
  activeCollectionPreferenceKey,
  collectionCompatibilityMode,
  loadMoreCollectionSession,
  resolveCollectionSession,
  selectActiveCollection,
} from '../collection/collectionSessionService';
import { recordCollectionMetric } from '../collection/collectionMetrics';
import {
  resetCollectionDataSession,
  subscribeToSharedCollection as subscribeToCollection,
  subscribeToSharedCollectionMembership as subscribeToCollectionMembership,
} from '../collection/collectionData';
import { rapidCaptureDb } from '../rapidCapture/rapidCaptureDb';

export function useAppData(binOpen: boolean, loadActiveInventory = true) {
  const { notify } = useFeedback();
  const alerts = useAlerts();
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [authStatus, setAuthStatus] =
    useState<AuthenticationStatus>('initializing');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [authoritativeItems, setAuthoritativeItems] = useState<Item[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [deletedItems, setDeletedItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemError, setItemError] = useState<string | null>(null);
  const [lastUsedLocation, setLastUsedLocation] = useState('');
  const [collectionResolution, setCollectionResolution] =
    useState<CollectionResolution | null>(null);
  const [collectionResetEpoch, setCollectionResetEpoch] = useState(0);
  const authChangeId = useRef(0);
  const authenticatedIdentity = useRef<string | null>(null);
  const sessionError = useRef<string | null>(null);
  const sessionNotice = useRef<string | null>(null);
  const inventoryEpoch = useRef(0);
  const userRef = useRef(user);
  const collectionResolutionRef = useRef(collectionResolution);
  const lifecyclePurgeKeys = useRef(new Set<string>());
  userRef.current = user;
  collectionResolutionRef.current = collectionResolution;
  const compatibilityMode = collectionCompatibilityMode();

  const clearUserData = useCallback(() => {
    inventoryEpoch.current += 1;
    userProfileResolverService.clearCache();
    setItems([]);
    setAuthoritativeItems([]);
    setLocations([]);
    setDeletedItems([]);
    setItemError(null);
    setLastUsedLocation('');
    setCollectionResetEpoch((value) => value + 1);
  }, []);

  const resolveCollections = useCallback(
    async (
      nextUser: AuthenticatedUser,
      changeId: number,
      initialization?: Promise<AuthenticatedUser>,
    ) => {
      const startedAt = performance.now();
      setCollectionResolution({
        status: 'loading',
        session: { userId: nextUser.uid, availableCollections: { items: [] } },
      });
      // Discovery only needs verified Auth, so overlap it with profile setup.
      // Neither result is published until setup and pending purges succeed.
      const [resolved, provisionedUser] = await Promise.all([
        resolveCollectionSession(
          nextUser.uid,
          firebaseCollectionRepository,
          compatibilityMode,
        ),
        initialization,
      ]);
      if (changeId !== authChangeId.current) return undefined;
      if (provisionedUser) setUser(provisionedUser);
      if (
        compatibilityMode === 'collection-only' &&
        resolved.status !== 'error'
      ) {
        const queuedCollectionIds = [
          ...new Set(
            (await rapidCaptureDb.list(nextUser.uid))
              .filter(
                ({ compatibilityMode: mode }) => mode === 'collection-only',
              )
              .map(({ collectionId }) => collectionId),
          ),
        ].slice(0, 100);
        const authorities = await Promise.all(
          queuedCollectionIds.map(async (collectionId) => {
            const missingAccess = (error: unknown) => {
              if (!isExpectedInaccessibleCollectionError(error)) throw error;
              return undefined;
            };
            const membership = await firebaseCollectionRepository
              .getMembership(collectionId, nextUser.uid)
              .catch(missingAccess);
            const collection =
              membership?.status === 'active'
                ? await firebaseCollectionRepository
                    .get(collectionId)
                    .catch(missingAccess)
                : undefined;
            return {
              collectionId,
              ...(membership?.status === 'active' &&
              collection &&
              collection.lifecycle !== 'deleting' &&
              collection.lifecycle !== 'deleted'
                ? { membershipVersion: membership.membershipVersion }
                : {}),
              ...(collection?.lifecycle === 'deleting' ||
              collection?.lifecycle === 'deleted'
                ? { deleting: true }
                : {}),
              ...(collection
                ? { lifecycleVersion: collection.lifecycleVersion }
                : {}),
            };
          }),
        ).catch(() => undefined);
        if (changeId !== authChangeId.current) return undefined;
        if (!authorities) {
          const failed: CollectionResolution = {
            status: 'error',
            session: {
              userId: nextUser.uid,
              availableCollections: { items: [] },
            },
          };
          setCollectionResolution(failed);
          setLoading(false);
          setAuthStatus('ready');
          return failed;
        }
        await rapidCaptureDb.reconcileCollectionAuthorities(
          nextUser.uid,
          authorities,
        );
      }
      if (changeId !== authChangeId.current) return undefined;
      const outcome =
        resolved.status === 'ready'
          ? 'ready'
          : resolved.status === 'revoked'
            ? 'revoked'
            : resolved.status === 'error'
              ? 'failed'
              : 'unavailable';
      recordCollectionMetric(
        'collection-session-resolution',
        performance.now() - startedAt,
        {
          mode: compatibilityMode,
          role: resolved.session.activeRole,
          outcome,
        },
      );
      recordCollectionMetric('inventory-route', 1, {
        mode: compatibilityMode,
        role: resolved.session.activeRole,
        outcome,
      });
      setCollectionResolution(resolved);
      setLoading(resolved.status === 'ready');
      setAuthStatus('ready');
      return resolved;
    },
    [compatibilityMode],
  );

  const initializeUser = useCallback(
    async (nextUser: AuthenticatedUser | null) => {
      const changeId = ++authChangeId.current;
      const previousIdentity = authenticatedIdentity.current;
      authenticatedIdentity.current = nextUser?.uid ?? null;
      resetCollectionDataSession(
        !nextUser ||
          Boolean(previousIdentity && previousIdentity !== nextUser.uid),
      );
      clearUserData();
      if (nextUser) {
        sessionError.current = null;
        sessionNotice.current = null;
      }
      setAuthError(sessionError.current);
      setAuthNotice(sessionNotice.current);
      setCollectionResolution(null);
      setUser(nextUser);

      if (!nextUser) {
        setLoading(false);
        setAuthStatus('signed-out');
        return;
      }
      if (!nextUser.emailVerified) {
        setLoading(false);
        setAuthStatus('verification-required');
        return;
      }

      setLoading(true);
      setAuthStatus('provisioning');
      try {
        const staleToken = await authService.hasStaleVerificationToken(
          nextUser.uid,
        );
        if (changeId !== authChangeId.current) return;
        if (staleToken) {
          // Return to sign-in so the next session starts with a verified token.
          sessionNotice.current =
            'Your email is verified. Sign in to continue.';
          await authService.signOut();
          return;
        }
        const initialization = Promise.all([
          accountProvisioningService.ensureUserProvisioned(nextUser),
          compatibilityMode === 'collection-only'
            ? rapidCaptureDb
                .retryPendingAuthorityPurges(nextUser.uid)
                .catch(() => {
                  recordCollectionMetric('terminal-offline-revocation', 1, {
                    mode: compatibilityMode,
                    source: 'membership-listener',
                    outcome: 'failed',
                  });
                  throw new Error(
                    'Pending collection authority cleanup failed.',
                  );
                })
            : undefined,
        ]).then(([provisionedUser]) => provisionedUser || nextUser);
        await resolveCollections(nextUser, changeId, initialization);
      } catch (error) {
        if (changeId !== authChangeId.current) return;
        logger.error('Account provisioning failed.', error);
        setAuthError(
          'qkIndex could not finish setting up this account. Check your connection and try again.',
        );
        setLoading(false);
        setAuthStatus('error');
      }
    },
    [clearUserData, compatibilityMode, resolveCollections],
  );

  useEffect(() => {
    const unsubscribe = authService.subscribe((nextUser) => {
      void initializeUser(nextUser);
    });
    void authService.completeGoogleRedirect().catch((error) => {
      logger.error('Google redirect sign-in failed:', error);
      setAuthError(authErrorMessage(error));
    });
    return () => {
      unsubscribe();
      resetCollectionDataSession();
    };
  }, [initializeUser]);

  useEffect(() => {
    const authority = collectionResolution?.authority;
    if (
      !loadActiveInventory ||
      !user ||
      authStatus !== 'ready' ||
      collectionResolution?.status !== 'ready' ||
      !authority
    )
      return;
    const epoch = inventoryEpoch.current;
    recordCollectionMetric('collection-subscription-start', 1, {
      mode: authority.compatibilityMode,
      role: collectionResolution.session.activeRole,
      source: 'locations',
    });
    const unsubscribe = subscribeToLocations(
      authority,
      (fetchedLocations) => {
        if (epoch === inventoryEpoch.current) setLocations(fetchedLocations);
      },
      () => {
        if (epoch !== inventoryEpoch.current) return;
        recordCollectionMetric('collection-subscription-error', 1, {
          mode: authority.compatibilityMode,
          role: collectionResolution.session.activeRole,
          source: 'locations',
          outcome: 'failed',
        });
        logger.error('Collection location subscription failed.');
        notify('Unable to load locations. Please retry.', 'error');
        setLocations([]);
      },
    );
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      recordCollectionMetric('collection-subscription-stop', 1, {
        mode: authority.compatibilityMode,
        role: collectionResolution.session.activeRole,
        source: 'locations',
      });
    };
  }, [user, authStatus, collectionResolution, notify, loadActiveInventory]);

  useEffect(() => {
    const authority = collectionResolution?.authority;
    if (
      !loadActiveInventory ||
      !user ||
      authStatus !== 'ready' ||
      collectionResolution?.status !== 'ready' ||
      !authority
    )
      return;
    const epoch = inventoryEpoch.current;
    recordCollectionMetric('collection-subscription-start', 1, {
      mode: authority.compatibilityMode,
      role: collectionResolution.session.activeRole,
      source: 'items',
    });
    const unsubscribe = subscribeToActiveItems(
      authority,
      (fetchedItems) => {
        if (epoch !== inventoryEpoch.current) return;
        setItemError(null);
        setItems(
          [...fetchedItems].sort(
            (left, right) =>
              Date.parse(right.lastEditedAt || right.createdAt) -
              Date.parse(left.lastEditedAt || left.createdAt),
          ),
        );
        setLoading(false);
      },
      () => {
        if (epoch !== inventoryEpoch.current) return;
        recordCollectionMetric('collection-subscription-error', 1, {
          mode: authority.compatibilityMode,
          role: collectionResolution.session.activeRole,
          source: 'items',
          outcome: 'failed',
        });
        logger.error('Collection item subscription failed.');
        setItemError('Unable to load items. Please retry.');
        setItems([]);
        setAuthoritativeItems([]);
        setLoading(false);
      },
      (fetchedItems) => {
        if (epoch === inventoryEpoch.current)
          setAuthoritativeItems(fetchedItems);
      },
    );
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      recordCollectionMetric('collection-subscription-stop', 1, {
        mode: authority.compatibilityMode,
        role: collectionResolution.session.activeRole,
        source: 'items',
      });
    };
  }, [user, authStatus, collectionResolution, loadActiveInventory]);

  useEffect(() => {
    const authority = collectionResolution?.authority;
    if (
      !user ||
      authStatus !== 'ready' ||
      collectionResolution?.status !== 'ready' ||
      !authority ||
      !binOpen
    )
      return;
    const epoch = inventoryEpoch.current;
    recordCollectionMetric('collection-subscription-start', 1, {
      mode: authority.compatibilityMode,
      role: collectionResolution.session.activeRole,
      source: 'deleted-items',
    });
    const unsubscribe = subscribeToDeletedItems(
      authority,
      (fetchedItems) => {
        if (epoch === inventoryEpoch.current) setDeletedItems(fetchedItems);
      },
      () => {
        if (epoch !== inventoryEpoch.current) return;
        recordCollectionMetric('collection-subscription-error', 1, {
          mode: authority.compatibilityMode,
          role: collectionResolution.session.activeRole,
          source: 'deleted-items',
          outcome: 'failed',
        });
        logger.error('Collection deleted-item subscription failed.');
        notify('Unable to load deleted items. Please retry.', 'error');
      },
    );
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      recordCollectionMetric('collection-subscription-stop', 1, {
        mode: authority.compatibilityMode,
        role: collectionResolution.session.activeRole,
        source: 'deleted-items',
      });
    };
  }, [user, authStatus, binOpen, collectionResolution, notify]);

  useEffect(() => {
    const activeCollectionId = collectionResolution?.session.activeCollectionId;
    const activeMembershipVersion =
      collectionResolution?.session.membershipVersion;
    const activeRole = collectionResolution?.session.activeRole;
    if (
      compatibilityMode !== 'collection-only' ||
      !user ||
      authStatus !== 'ready' ||
      collectionResolution?.status !== 'ready' ||
      !activeCollectionId ||
      activeMembershipVersion === undefined ||
      !activeRole
    )
      return undefined;
    let active = true;
    let reconciling = false;
    const reconcile = async (
      next:
        | {
            status: 'active' | 'revoked';
            membershipVersion: number;
            role: 'owner' | 'administrator' | 'editor' | 'viewer';
          }
        | undefined,
      listenerFailed = false,
    ) => {
      if (!active || reconciling) return;
      const current = collectionResolutionRef.current;
      if (
        !current ||
        current.session.activeCollectionId !== activeCollectionId ||
        (next?.status === 'active' &&
          next.membershipVersion === current.session.membershipVersion &&
          next.role === current.session.activeRole)
      )
        return;
      reconciling = true;
      const revoked = !listenerFailed && (!next || next.status === 'revoked');
      const observedVersion =
        next?.membershipVersion ?? activeMembershipVersion + 1;
      const changeId = ++authChangeId.current;
      clearUserData();
      setCollectionResolution({
        status: 'loading',
        session: {
          ...current.session,
          activeCollectionId: undefined,
          activeRole: undefined,
          membershipVersion: undefined,
        },
      });
      if (revoked) {
        localStorage.removeItem(
          `lastUsedLocation_${user.uid}_${activeCollectionId}`,
        );
        if (
          localStorage.getItem(activeCollectionPreferenceKey(user.uid)) ===
          activeCollectionId
        ) {
          localStorage.removeItem(activeCollectionPreferenceKey(user.uid));
        }
      }
      if (!listenerFailed) {
        try {
          await rapidCaptureDb.purgeCollectionAuthority(
            user.uid,
            activeCollectionId,
            observedVersion,
            revoked ? 'revoked' : 'membership-changed',
          );
        } catch {
          recordCollectionMetric('terminal-offline-revocation', 1, {
            mode: compatibilityMode,
            role: activeRole,
            source: 'membership-listener',
            outcome: 'failed',
          });
          logger.error('Collection authority cleanup failed.');
          reconciling = false;
          return;
        }
      }
      recordCollectionMetric(
        listenerFailed
          ? 'collection-subscription-error'
          : revoked
            ? 'terminal-offline-revocation'
            : 'membership-version-mismatch',
        1,
        {
          mode: compatibilityMode,
          role: activeRole,
          source: 'membership-listener',
          outcome: revoked ? 'revoked' : listenerFailed ? 'failed' : 'changed',
        },
      );
      if (
        authChangeId.current === changeId &&
        userRef.current?.uid === user.uid
      ) {
        await resolveCollections(user, changeId);
      }
      reconciling = false;
    };
    const unsubscribe = subscribeToCollectionMembership(
      activeCollectionId,
      user.uid,
      (membership) => {
        void reconcile(membership);
      },
      () => {
        void reconcile(undefined, true);
      },
    );
    return () => {
      active = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [
    authStatus,
    clearUserData,
    collectionResolution,
    compatibilityMode,
    resolveCollections,
    user,
  ]);

  useEffect(() => {
    const activeCollectionId = collectionResolution?.session.activeCollectionId;
    const membershipVersion = collectionResolution?.session.membershipVersion;
    if (
      compatibilityMode !== 'collection-only' ||
      !user ||
      authStatus !== 'ready' ||
      collectionResolution?.status !== 'ready' ||
      !activeCollectionId ||
      membershipVersion === undefined
    )
      return undefined;
    let active = true;
    const revoke = (version?: number, listenerFailed = false) => {
      if (!active) return;
      const key = `${activeCollectionId}:${version ?? 'unavailable'}`;
      if (!listenerFailed && lifecyclePurgeKeys.current.has(key)) return;
      if (!listenerFailed) lifecyclePurgeKeys.current.add(key);
      const changeId = ++authChangeId.current;
      clearUserData();
      setCollectionResolution({
        status: 'loading',
        session: { userId: user.uid, availableCollections: { items: [] } },
      });
      void (async () => {
        try {
          // Listener errors do not prove access loss. Reconciliation below only
          // purges queued photos after authoritative reads confirm it.
          if (!listenerFailed) {
            await Promise.all([
              rapidCaptureDb.purgeCollectionAuthority(
                user.uid,
                activeCollectionId,
                membershipVersion,
                'collection-deleting',
              ),
              createInventoryMetadataCache(user.uid).remove(activeCollectionId),
            ]);
          }
        } catch {
          lifecyclePurgeKeys.current.delete(key);
          logger.error('Collection deletion authority cleanup failed.');
        }
        if (
          userRef.current?.uid === user.uid &&
          authChangeId.current === changeId
        )
          await resolveCollections(user, changeId);
      })();
    };
    const unsubscribe = subscribeToCollection(
      activeCollectionId,
      user.uid,
      (collection) => {
        if (
          !collection ||
          collection.lifecycle === 'deleting' ||
          collection.lifecycle === 'deleted'
        )
          revoke(collection?.lifecycleVersion);
      },
      () => {
        revoke(undefined, true);
      },
    );
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [
    authStatus,
    clearUserData,
    collectionResolution,
    compatibilityMode,
    resolveCollections,
    user,
  ]);

  useEffect(() => {
    const activeCollectionId = collectionResolution?.session.activeCollectionId;
    if (!user || authStatus !== 'ready' || !activeCollectionId) {
      setLastUsedLocation('');
      return;
    }
    setLastUsedLocation(
      localStorage.getItem(
        `lastUsedLocation_${user.uid}_${activeCollectionId}`,
      ) || '',
    );
  }, [user, authStatus, collectionResolution, compatibilityMode]);

  const updateLastUsedLocation = useCallback(
    async (location: string) => {
      const activeCollectionId =
        collectionResolution?.session.activeCollectionId;
      if (!user || authStatus !== 'ready' || !activeCollectionId || !location)
        return;
      setLastUsedLocation(location);
      localStorage.setItem(
        `lastUsedLocation_${user.uid}_${activeCollectionId}`,
        location,
      );
    },
    [user, authStatus, collectionResolution],
  );

  const retryCollectionSession = useCallback(async () => {
    if (!user?.emailVerified) return undefined;
    const changeId = ++authChangeId.current;
    clearUserData();
    setLoading(true);
    return resolveCollections(user, changeId);
  }, [clearUserData, resolveCollections, user]);

  const captureCollectionSessionContext = useCallback(() => {
    const currentUser = userRef.current;
    const currentResolution = collectionResolutionRef.current;
    if (!currentUser?.emailVerified || !currentResolution) return undefined;
    return {
      changeId: authChangeId.current,
      userId: currentUser.uid,
      collectionId: currentResolution.session.activeCollectionId,
    };
  }, []);

  const retryCollectionSessionIfCurrent = useCallback(
    async (expected: {
      changeId: number;
      userId: string;
      collectionId?: string;
    }) => {
      const currentUser = userRef.current;
      const currentResolution = collectionResolutionRef.current;
      if (
        authChangeId.current !== expected.changeId ||
        currentUser?.uid !== expected.userId ||
        currentResolution?.session.activeCollectionId !== expected.collectionId
      ) {
        return undefined;
      }
      const changeId = ++authChangeId.current;
      clearUserData();
      setLoading(true);
      return resolveCollections(currentUser, changeId);
    },
    [clearUserData, resolveCollections],
  );

  const loadMoreCollections = useCallback(async () => {
    const current = collectionResolutionRef.current;
    if (!current?.session.availableCollections.nextCursor) return;
    const changeId = authChangeId.current;
    const expanded = await loadMoreCollectionSession(
      current,
      firebaseCollectionRepository,
      compatibilityMode,
    );
    if (changeId !== authChangeId.current) return;
    setCollectionResolution(expanded);
    setLoading(expanded.status === 'ready');
  }, [compatibilityMode]);

  const switchCollection = useCallback(
    async (collectionId: string) => {
      if (
        !user ||
        !collectionResolution ||
        collectionResolution.status === 'loading'
      )
        return;
      const startedAt = performance.now();
      const changeId = ++authChangeId.current;
      clearUserData();
      setLoading(true);
      setCollectionResolution({
        ...collectionResolution,
        status: 'loading',
        authority: undefined,
        session: {
          ...collectionResolution.session,
          activeCollectionId: undefined,
          activeRole: undefined,
          membershipVersion: undefined,
        },
      });
      const selected = await selectActiveCollection(
        collectionResolution,
        collectionId,
        firebaseCollectionRepository,
        compatibilityMode,
      );
      if (changeId !== authChangeId.current) return;
      recordCollectionMetric(
        'collection-switch-latency',
        performance.now() - startedAt,
        {
          mode: compatibilityMode,
          role: selected.session.activeRole,
          outcome:
            selected.status === 'ready'
              ? 'ready'
              : selected.status === 'revoked'
                ? 'revoked'
                : selected.status === 'error'
                  ? 'failed'
                  : 'unavailable',
        },
      );
      setCollectionResolution(selected);
      setLoading(selected.status === 'ready');
    },
    [clearUserData, collectionResolution, compatibilityMode, user],
  );

  // Post-mutation refreshes must never turn a successful mutation into a retry.
  // They also must not restore an account superseded by an Auth event.
  const refreshAuthenticatedSession = useCallback(async () => {
    const changeId = authChangeId.current;
    const actor = authService.currentUser();
    try {
      if (!actor)
        throw Object.assign(new Error('Session expired.'), {
          code: 'auth/user-token-expired',
        });
      const refreshed = await authService.refreshCurrentUser();
      if (
        changeId !== authChangeId.current ||
        authService.currentUser()?.uid !== actor.uid
      )
        return;
      await initializeUser(refreshed);
      return refreshed;
    } catch (error) {
      const current = authService.currentUser();
      // Firebase can emit signed-out before rejecting a revoked token refresh.
      // A newer signed-in session, however, must never be cleared by this request.
      if (changeId !== authChangeId.current && current) return;
      if (current && current.uid !== actor?.uid) return;
      if (isExpiredAuthSession(error)) {
        sessionError.current = authErrorMessage(error);
        const reset = initializeUser(null);
        const resetChangeId = authChangeId.current;
        await reset;
        if (
          current &&
          authChangeId.current === resetChangeId &&
          authService.currentUser()?.uid === current.uid
        ) {
          try {
            await authService.signOut();
          } catch {
            // The UI is already fenced. Keep the recovery message even if local
            // Firebase persistence cannot finish signing out.
          }
        }
      } else if (changeId === authChangeId.current) {
        if (actor)
          alerts?.notice(actor.uid, {
            id: 'refresh-failed',
            title: 'Changes could not be refreshed',
            message:
              'Check your connection and refresh the page. Completed changes are saved.',
            severity: 'warning',
          });
      }
    }
  }, [initializeUser, alerts]);

  const refreshVerification = useCallback(async () => {
    const refreshed = await refreshAuthenticatedSession();
    return refreshed?.emailVerified === true;
  }, [refreshAuthenticatedSession]);

  const verificationDeliveryFailed = useSyncExternalStore(
    authService.subscribeVerificationDelivery,
    () => user !== null && authService.verificationDeliveryFailed(user.uid),
  );

  const retryProvisioning = useCallback(async () => {
    await initializeUser(authService.currentUser());
  }, [initializeUser]);

  const refreshSession = useCallback(async () => {
    await refreshAuthenticatedSession();
  }, [refreshAuthenticatedSession]);

  const signOut = useCallback(async () => {
    ++authChangeId.current;
    authenticatedIdentity.current = null;
    resetCollectionDataSession(true);
    clearUserData();
    setCollectionResolution(null);
    setUser(null);
    setAuthStatus('signed-out');
    await authService.signOut();
  }, [clearUserData]);

  const updateUsername = useCallback((newUsername: string) => {
    userProfileResolverService.clearCache();
    setUser((prev) =>
      prev
        ? {
            ...prev,
            username: newUsername,
            usernameUpdatedAt: new Date(),
          }
        : null,
    );
  }, []);

  return {
    user,
    authStatus,
    authError,
    authNotice,
    authInitialized: authStatus !== 'initializing',
    items,
    authoritativeItems,
    setItems,
    locations,
    deletedItems,
    setDeletedItems,
    loading,
    setLoading,
    itemError,
    collectionStatus: collectionResolution?.status || 'idle',
    collectionResetEpoch,
    collectionSession: collectionResolution?.session,
    activeCollectionId: collectionResolution?.session.activeCollectionId,
    activeCollectionRole: collectionResolution?.session.activeRole,
    activeMembershipVersion: collectionResolution?.session.membershipVersion,
    inventoryAuthority: collectionResolution?.authority,
    compatibilityMode,
    retryCollectionSession,
    captureCollectionSessionContext,
    retryCollectionSessionIfCurrent,
    loadMoreCollections,
    switchCollection,
    lastUsedLocation,
    updateLastUsedLocation,
    updateUsername,
    refreshVerification,
    resendVerification: authService.resendVerification,
    verificationDeliveryFailed,
    retryProvisioning,
    refreshSession,
    signOut,
  };
}
